/**
 * EmptyState — shared, reusable empty-state for list pages.
 *
 * Replaces the bare "No data" / `<p>No invoices</p>` patterns scattered across
 * the app with a friendly icon + title + description + optional CTA. Designed
 * to slot into a TableCell (`compact` variant) or sit as a standalone block
 * inside a Card.
 *
 * Usage:
 *   <EmptyState
 *     icon={FileText}
 *     title="No invoices yet"
 *     description="Generate your first invoice from approved timesheets."
 *     action={<Button onClick={...}>Generate Invoice</Button>}
 *     testid="invoices-empty"
 *   />
 */
import React from 'react';

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  compact = false,
  testid = 'empty-state',
  className = '',
}) {
  return (
    <div
      data-testid={testid}
      className={`flex flex-col items-center justify-center text-center ${compact ? 'py-8 px-4' : 'py-14 px-6'} ${className}`}
    >
      {Icon && (
        <div className={`${compact ? 'h-10 w-10' : 'h-14 w-14'} rounded-full bg-slate-100 flex items-center justify-center mb-3`}>
          <Icon className={`${compact ? 'h-5 w-5' : 'h-6 w-6'} text-slate-500`} aria-hidden />
        </div>
      )}
      {title && (
        <div className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-slate-900`}>{title}</div>
      )}
      {description && (
        <div className={`${compact ? 'text-xs' : 'text-sm'} text-slate-500 mt-1 max-w-md`}>{description}</div>
      )}
      {(action || secondaryAction) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

export default EmptyState;
